'use client';

import React, { Component, ReactNode } from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center p-8 gap-6 min-h-[400px]">
          <div 
            className="flex flex-col items-center justify-center p-8 gap-6 w-full max-w-md"
            style={{
              borderRadius: '12px',
              background: 'var(--color-mono-50, #f8f8ff)',
            }}
          >
            {/* Error Icon */}
            <div 
              className="flex items-center justify-center"
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'var(--color-danger-100, #ffc7c7)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                  stroke="var(--color-danger-500, #ea3d3d)" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            
            <div className="flex flex-col items-center gap-2">
              <h3 className="xl-text font-medium text-action-900 text-center">
                Something went wrong
              </h3>
              
              <p className="body-base text-mono-700 text-center">
                We encountered an unexpected error. Please try refreshing the page.
              </p>
            </div>

            <div className="flex gap-4">
              <button 
                className="btn-primary" 
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => this.setState({ hasError: false })}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;